/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 * Core profile presentation for hosts that do not own Core company switching.
 */
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Icon } from './ComponentIcon';
import './ProfileDropdown.css';

export interface ProfileLanguageSelectProps<Value extends string> {
  label: string;
  value: Value;
  options: ReadonlyArray<{ value: Value; label: string }>;
  onChange: (value: Value) => void;
  /** Controlled opening also supports Core's product tour. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/** Presentation only: the host owns supported languages, translations and persistence. */
export function ProfileLanguageSelect<Value extends string>({
  label, value, options, onChange, open: controlledOpen, onOpenChange,
}: ProfileLanguageSelectProps<Value>) {
  const [localOpen, setLocalOpen] = useState(false);
  const open = controlledOpen ?? localOpen;
  const setOpen = useCallback((next: boolean) => {
    if (controlledOpen === undefined) setLocalOpen(next);
    onOpenChange?.(next);
  }, [controlledOpen, onOpenChange]);
  const selectedIndex = Math.max(0, options.findIndex(option => option.value === value));
  const [activeIndex, setActiveIndex] = useState(selectedIndex);
  const [availableHeight, setAvailableHeight] = useState<number>();
  const id = useId();
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLUListElement>(null);
  useLayoutEffect(() => {
    if (!open) return;
    const measure = () => {
      const top = list.current?.getBoundingClientRect().top;
      if (top !== undefined) setAvailableHeight(Math.max(0, window.innerHeight - top - 16));
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [open]);
  useEffect(() => {
    if (!open) return;
    setActiveIndex(selectedIndex);
    list.current?.focus();
  }, [open, selectedIndex]);
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open, setOpen]);
  useEffect(() => {
    if (open) list.current?.children[activeIndex]?.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex]);
  const choose = (index: number) => {
    const option = options[index];
    if (!option) return;
    setOpen(false);
    trigger.current?.focus();
    onChange(option.value);
  };
  return <div className="profile-dropdown-language-section">
    <div ref={root} className="profile-language-select-wrap" data-tour-id="profile-language-preference"
      onBlur={event => {
        if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget as Node)) setOpen(false);
      }}>
      <button ref={trigger} type="button" className="profile-language-select-trigger" aria-label={label}
        aria-expanded={open} aria-haspopup="listbox" aria-controls={open ? id : undefined}
        onClick={() => setOpen(!open)} onKeyDown={event => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            setOpen(true);
          }
        }}>
        <Icon name="language" size={18} className="profile-language-globe" />
        <span className="profile-language-select-label">{options.find(option => option.value === value)?.label ?? value}</span>
        <Icon name="expandMore" size={18} className="profile-language-select-chevron" />
      </button>
      {open && <ul ref={list} id={id} className="profile-language-select-dropdown" role="listbox"
        style={{ maxHeight: availableHeight === undefined ? undefined : `min(280px, 60vh, ${availableHeight}px)` }}
        aria-label={label} tabIndex={-1} aria-activedescendant={`${id}-${activeIndex}`}
        onKeyDown={event => {
          if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            setOpen(false);
            trigger.current?.focus();
          } else if (event.key === 'Tab') {
            setOpen(false);
            trigger.current?.focus();
          } else if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            choose(activeIndex);
          } else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
            event.preventDefault();
            setActiveIndex(index => event.key === 'Home' ? 0 : event.key === 'End' ? options.length - 1
              : Math.max(0, Math.min(options.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1))));
          }
        }}>
        {options.map((option, index) => <li key={option.value} id={`${id}-${index}`} role="option"
          aria-selected={option.value === value}
          className={`profile-language-option${option.value === value ? ' selected' : ''}${index === activeIndex ? ' is-active' : ''}`}
          onClick={() => choose(index)}>
          {option.value === value && <span className="profile-language-option-check">✓</span>}
          {option.label}
        </li>)}
      </ul>}
    </div>
  </div>;
}

export interface ProfileMenuSurfaceProps {
  label: string;
  name: string;
  email?: string;
  triggerAvatar: ReactNode;
  avatar: ReactNode;
  children: ReactNode;
}

export function ProfileMenuSurface({ label, name, email, triggerAvatar, avatar, children }: ProfileMenuSurfaceProps) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      setOpen(false);
      trigger.current?.focus();
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);
  return <div className="profile-menu-container" ref={root} onBlur={event => {
    if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget as Node)) setOpen(false);
  }}>
    <button ref={trigger} type="button" className="profile-menu-button" aria-label={label}
      aria-expanded={open} aria-controls={id} onClick={() => setOpen(value => !value)}>{triggerAvatar}</button>
    {open && <div id={id} className="profile-dropdown" role="region" aria-label={label}>
      <div className="profile-dropdown-header">
        {avatar}
        <div className="profile-dropdown-info">
          <div className="profile-dropdown-name">{name}</div>
          <div className="profile-dropdown-email">{email}</div>
        </div>
      </div>
      <div className="profile-dropdown-divider" />
      <div className="profile-dropdown-menu">{children}</div>
    </div>}
  </div>;
}
